import React, { useEffect, ReactNode } from 'react'
import moment from 'moment'
import { Message } from './types'
import callIcon from 'images/call-summary-modal.svg'
import playButtonIcon from 'images/play-button.svg'
import pauseButtonIcon from 'images/pause-button.svg'
import { useSimpleAudioController } from 'hooks/use-simple-audio-controller'
import { useAudioContext } from 'contexts/AudioContext'
import CallProgressBar from './call-progress-bar'
import skipForwardIcon from 'images/skip-forward.svg'
import skipBackwardIcon from 'images/skip-backward.svg'

interface CallSentence {
  id: number
  paragraph_id: number
  recording_id: number
  speaker_id: number
  text: string
  start: number
  end: number
}

interface CallParagraph {
  id: number
  start?: number
  end?: number
  speaker: {
    id: number
    friendly_name: string
    image_url?: string | null
    user_contact_id?: number | null
    user_id?: number | null
  }
  sentences: CallSentence[]
}

interface CallModalProps {
  show: boolean
  onClose: () => void
  message: Message
  title: string
  children?: ReactNode
  paragraphs: CallParagraph[]
  hasTranscription: boolean
  isVoicemail: boolean
  callDuration: number
  audioUrl?: string
  audioId: string
}

const TranscriptWithAudio: React.FC<CallModalProps> = ({
  show,
  onClose,
  message,
  title,
  children,
  paragraphs,
  hasTranscription,
  isVoicemail,
  callDuration,
  audioUrl,
  audioId,
}) => {
  const { pauseCurrentAudio } = useAudioContext()

  const audioController = useSimpleAudioController({
    audioUrl: audioUrl || '',
    duration: callDuration,
    audioId,
  })

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }

  const handleClose = () => {
    pauseCurrentAudio()
    onClose()
  }

  useEffect(() => {
    if (!show) {
      pauseCurrentAudio()
    }
  }, [show, pauseCurrentAudio])

  if (!show) return null

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        style={{
          width: '100%',
          maxWidth: 'min(700px, 95vw)',
          height: 'auto',
          maxHeight: '90vh',
          margin: '1rem auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content bg-dark text-white"
          style={{
            borderRadius: '24px',
            height: '100%',
            maxHeight: '90vh',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="modal-header border-0 pb-0" style={{ flexShrink: 0 }}>
            <h5 className="modal-title fw-bold text-white">{title}</h5>
            <button
              type="button"
              className="btn btn-link p-0 no-focus-shadow"
              onClick={handleClose}
              style={{
                boxShadow: 'none',
                border: 'none',
                outline: 'none',
              }}
            >
              <ion-icon
                name="close"
                style={{ fontSize: '20px', color: 'white' }}
              />
            </button>
          </div>

          <div
            className="modal-body"
            style={{
              padding: '1rem',
              paddingTop: '1rem',
              paddingBottom: '1rem',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Custom content */}
            {children}

            {/* Transcript Section */}
            {hasTranscription && paragraphs.length > 0 ? (
              <div className="mb-3">
                <h6
                  className="fw-bold mb-3 text-white"
                  style={{ fontSize: 'clamp(16px, 4.5vw, 18px)' }}
                >
                  Transcript
                </h6>
                <div
                  className="transcript-container"
                  style={{
                    maxHeight: children ? '30vh' : '40vh',
                    overflowY: 'auto',
                    paddingRight: '1rem',
                    marginRight: '-1rem',
                    marginBottom: '1rem',
                  }}
                >
                  {paragraphs.map((paragraph: CallParagraph, index: number) => (
                    <div
                      key={paragraph.id || index}
                      className="mb-3 pb-3 border-start border-3 border-primary ps-3"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong
                          style={{
                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                            color: '#4EA6FF',
                          }}
                        >
                          {paragraph.speaker.friendly_name ||
                            `Speaker ${paragraph.speaker.id}`}
                        </strong>
                        <small
                          className="text-light"
                          style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                        >
                          {formatTime(
                            Math.floor(
                              paragraph.sentences[0]?.start || index * 30
                            )
                          )}
                        </small>
                      </div>
                      <div
                        className="text-white"
                        style={{
                          fontSize: 'clamp(14px, 4vw, 16px)',
                          lineHeight: '1.4',
                        }}
                      >
                        {paragraph.sentences
                          .sort(
                            (a: CallSentence, b: CallSentence) =>
                              a.start - b.start
                          )
                          .map((sentence: CallSentence) => sentence.text)
                          .join(' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div
                className="text-center text-white"
                style={{ padding: '2rem' }}
              >
                <h6>No transcript available</h6>
                <p style={{ opacity: 0.7 }}>
                  {hasTranscription
                    ? 'Transcript is empty.'
                    : "No transcription available yet - We're working on it!"}
                </p>
              </div>
            )}
          </div>

          {/* Audio Player Container */}
          <div
            style={{
              flexShrink: 0,
              width: '100%',
              minHeight: '156px',
              height: 'auto',
              backgroundColor: '#212529',
              borderRadius: '0 0 24px 24px',
              padding: '1rem',
            }}
          >
            {/* Call Info Header */}
            <div className="d-flex align-items-center mb-3">
              <div
                className="d-flex align-items-center justify-content-center me-3"
                style={{
                  width: 'clamp(32px, 8vw, 40px)',
                  height: 'clamp(32px, 8vw, 40px)',
                  flexShrink: 0,
                  backgroundColor: isVoicemail ? '#e91e63' : 'transparent',
                  borderRadius: isVoicemail ? '50%' : '0',
                  padding: isVoicemail ? '8px' : '0',
                }}
              >
                {isVoicemail ? (
                  <ion-icon
                    name="mic"
                    style={{ fontSize: '20px', color: 'white' }}
                  />
                ) : (
                  <img
                    src={callIcon}
                    alt="Call Icon"
                    style={{ width: '100%', height: '100%' }}
                  />
                )}
              </div>
              <div>
                <div
                  className="text-white fw-bold"
                  style={{ fontSize: 'clamp(14px, 4vw, 16px)' }}
                >
                  {isVoicemail
                    ? `Voicemail from ${
                        message.call?.user_contact?.contact?.full_name ||
                        message.call?.user_contact?.contact?.phone_number ||
                        'Unknown'
                      }`
                    : `Call with ${
                        message.call?.user_contact?.contact?.full_name ||
                        message.call?.user_contact?.contact?.phone_number ||
                        'Unknown'
                      }`}
                </div>
                <div
                  className="text-light"
                  style={{ fontSize: 'clamp(12px, 3.5vw, 14px)' }}
                >
                  {moment(message.sent_at).format('M/D/YYYY, h:mm A')}
                </div>
              </div>
            </div>

            {/* Progress Bar / Waveform */}
            <div className="mb-3">
              <CallProgressBar
                progressRatio={audioController.getProgressRatio()}
                isVoicemail={isVoicemail}
              />
            </div>

            {/* Time Display and Controls */}
            <div className="d-flex justify-content-between align-items-center">
              <span
                className="text-light"
                style={{ fontSize: 'clamp(12px, 3.5vw, 14px)' }}
              >
                {audioController.getDisplayTime()}
              </span>

              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn text-white p-1 me-2"
                  style={{
                    fontSize: 'clamp(18px, 5vw, 20px)',
                    backgroundColor: 'transparent',
                  }}
                  onClick={audioController.skipBackward}
                >
                  <img
                    src={skipBackwardIcon}
                    alt="Skip Backward"
                    style={{
                      width: 'clamp(24px, 7vw, 34px)',
                      height: 'clamp(24px, 7vw, 34px)',
                    }}
                  />
                </button>
                <button
                  type="button"
                  className="btn d-flex align-items-center justify-content-center me-2"
                  style={{
                    width: 'clamp(36px, 8vw, 40px)',
                    height: 'clamp(36px, 8vw, 40px)',
                    fontSize: 'clamp(18px, 5vw, 20px)',
                    backgroundColor: 'transparent',
                  }}
                  onClick={audioController.togglePlayback}
                >
                  <img
                    src={
                      audioController.isPlaying
                        ? pauseButtonIcon
                        : playButtonIcon
                    }
                    alt={audioController.isPlaying ? 'Pause' : 'Play'}
                    style={{
                      width: 'clamp(24px, 7vw, 34px)',
                      height: 'clamp(24px, 7vw, 34px)',
                    }}
                  />
                </button>
                <button
                  type="button"
                  className="btn text-white p-1"
                  style={{
                    fontSize: 'clamp(18px, 5vw, 20px)',
                    backgroundColor: 'transparent',
                  }}
                  onClick={audioController.skipForward}
                >
                  <img
                    src={skipForwardIcon}
                    alt="Skip Forward"
                    style={{
                      width: 'clamp(24px, 7vw, 34px)',
                      height: 'clamp(24px, 7vw, 34px)',
                    }}
                  />
                </button>
              </div>

              <span
                className="text-light"
                style={{ fontSize: 'clamp(12px, 3.5vw, 14px)' }}
              >
                {audioController.formatTime(audioController.duration)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TranscriptWithAudio
